import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { selectHasPermission } from "../../store/slices/authSlice";
import {
  fetchFamilyFeedbacks,
  deleteFamilyFeedback,
} from "../../store/slices/familyFeedbackSlice";

const FamilyFeedbackList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { caseId, patientId } = location.state || {};

  const canManage = useSelector((s) => selectHasPermission(s, "family-feedback-manage"));
  const canCreate = useSelector((s) => selectHasPermission(s, "family-feedback-create"));
  const canEdit = useSelector((s) => selectHasPermission(s, "family-feedback-edit"));
  const canDelete = useSelector((s) => selectHasPermission(s, "family-feedback-delete"));

  const { familyFeedbacks, isLoading } = useSelector((s) => s.familyFeedback);
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (patientId) dispatch(fetchFamilyFeedbacks(patientId));
  }, [dispatch, patientId]);

  const handleAdd = () => navigate("/phase04/family-feedback/add", { state: location.state });
  const handleEdit = (id) => navigate(`/phase04/family-feedback/edit/${id}`, { state: location.state });
  const handlePrint = (id) => navigate(`/phase04/family-feedback/print/${id}`, { state: location.state });

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: "Are you sure?", text: "You want to delete this Family Feedback?", icon: "warning", showCancelButton: true, confirmButtonColor: "#3085d6", cancelButtonColor: "#d33", confirmButtonText: "Yes, delete it!", cancelButtonText: "Cancel" });
    if (result.isConfirmed) {
      try {
        await dispatch(deleteFamilyFeedback(id)).unwrap();
        if (patientId) dispatch(fetchFamilyFeedbacks(patientId));
        Swal.fire("Deleted!", "Record has been deleted.", "success");
      } catch (err) {
        Swal.fire("Error!", err || "Failed to delete record.", "error");
      }
    }
  };

  const filteredRecords = (familyFeedbacks || []).filter((r) => {
    if (caseId && r.case_id !== caseId) return false;
    const patientName = `${r.patient?.first_name || ""} ${r.patient?.last_name || ""}`.toLowerCase();
    const caseNumber = (r.case?.case_number || "").toLowerCase();
    const creatorName = (r.creator?.name || "").toLowerCase();
    const searchStr = filterText.toLowerCase();
    return patientName.includes(searchStr) || caseNumber.includes(searchStr) || creatorName.includes(searchStr);
  });

  const totalRows = filteredRecords.length;

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Family Feedback Records", 14, 10);
    const columns = ["#", "Patient Name", "Case Number", "Created By", "Created At"];
    const rows = filteredRecords.map((r, i) => [
      i + 1,
      `${r.patient?.first_name || ""} ${r.patient?.last_name || ""}`.trim() || "-",
      r.case?.case_number ?? "-",
      r.creator?.name ?? "-",
      new Date(r.created_at).toLocaleDateString("en-GB"),
    ]);
    autoTable(doc, { head: [columns], body: rows, startY: 20 });
    doc.save("family_feedback_records.pdf");
  };

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select className="form-select form-select-sm" style={{ width: "80px" }} value={rowsPerPage} onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}>
            {[5, 10, 15, 20].map((o) => (<option key={o} value={o}>{o}</option>))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm border" onClick={() => onChangePage(Math.max(1, currentPage - 1), rowCount)} disabled={currentPage === 1}><i className="ri-arrow-left-s-line"></i></button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-sm border" onClick={() => onChangePage(Math.min(totalPages, currentPage + 1), rowCount)} disabled={currentPage === totalPages}><i className="ri-arrow-right-s-line"></i></button>
        </div>
      </div>
    );
  };

  const columns = [
    { name: "S.No", cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage, width: "70px" },
    { name: "Patient Name", selector: (r) => `${r.patient?.first_name || ""} ${r.patient?.last_name || ""}`.trim(), sortable: true },
    { name: "Case Number", selector: (r) => r.case?.case_number || "-", sortable: true },
    { name: "Created By", selector: (r) => r.creator?.name || "-", sortable: true },
    { name: "Created At", selector: (r) => new Date(r.created_at).toLocaleDateString("en-GB"), sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1 justify-content-center">
          <button className="btn btn-sm btn-outline-info" onClick={() => handlePrint(row.id)} title="Print Preview"><i className="ri-printer-line"></i></button>
          {canEdit && (<button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(row.id)} title="Edit Record"><i className="ri-edit-line"></i></button>)}
          {canDelete && (<button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)} title="Delete Record"><i className="ri-delete-bin-line"></i></button>)}
        </div>
      ),
      width: "160px",
    },
  ];

  if (!patientId) return <Navigate to="/cases" state={{ error: "No patient selected." }} replace />;
  if (!canManage) return <Navigate to="/dashboard" state={{ error: "You do not have permission to manage Family Feedback." }} replace />;

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <div className="dropdown">
                <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown"><i className="ri-download-2-line me-1"></i> Export</button>
                <ul className="dropdown-menu">
                  <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
                  <li><CSVLink data={filteredRecords} filename="family_feedback_records.csv" className="dropdown-item"><i className="ri-file-excel-line me-1 text-success"></i> CSV</CSVLink></li>
                </ul>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <input type="text" className="form-control form-control-sm" placeholder="Search..." value={filterText} onChange={(e) => setFilterText(e.target.value)} style={{ width: "200px" }} />
              {canCreate && (<button className="btn btn-primary btn-sm" onClick={handleAdd}><i className="ri-add-line me-1"></i> Add Record</button>)}
            </div>
          </div>
          <div className="card-body p-0">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
              <DataTable
                columns={columns}
                data={filteredRecords}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationComponent={(props) => (
                  <CustomPagination {...props} currentPage={currentPage} rowsPerPage={rowsPerPage} onChangePage={(page) => setCurrentPage(page)} onChangeRowsPerPage={(size) => { setRowsPerPage(size); setCurrentPage(1); }} />
                )}
                noDataComponent={<div className="p-4 text-center text-muted">No Family Feedback records found. Click 'Add Record' to create one.</div>}
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default FamilyFeedbackList;
